import { AxiosError, AxiosInstance } from 'axios';
import { createApiClient } from '../http/client';
import {
  AddNoteRequest,
  AgentAvailability,
  AgentDirectoryEntry,
  AssignAgentRequest,
  CallHistoryQuery,
  CallLog,
  CallMetricsResponse,
  Conference,
  ConferenceNote,
  InitiateTransferResponse,
  ListTrackingNumbersParams,
  RateCallRequest,
  RawAgentDirectoryEntry,
  SearchAgentsParams,
  TrackingNumberDetail,
  TrackingNumberListItem,
  TransferActionResponse,
  TransferCallRequest,
  UnassignAgentRequest,
  UpdateAgentAvailabilityRequest,
  UpdateTrackingNumberRequest,
  CreateTrackingNumberRequest,
} from '../../interfaces/calls';
import { ApiResponse } from '../../interfaces/common';
import {
  Agent,
  FindAllVoicemailsParams,
  UpdateVoicemailParams,
  VoicemailLog,
} from '../../interfaces/voicemail';
import { AuthService } from '../auth';

const APPOINTMENTS_API_BASE_URL = process.env.REACT_APP_PUBLIC_APPOINTMENTS_API_BASE_URL;

class VoiceService {
  private axios: AxiosInstance;

  constructor() {
    this.axios = createApiClient({
      baseURL: APPOINTMENTS_API_BASE_URL,
    });
  }

  private getAuthHeaders() {
    return {
      Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
    };
  }

  public async generateAccessToken(): Promise<ApiResponse<{ token: string }>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<{ token: string }>>(`/calls/token`, {
          headers: this.getAuthHeaders(),
        })
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      console.error('Error generating access token:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to generate access token',
      };
    }
  }

  public async searchCalls(query: CallHistoryQuery): Promise<ApiResponse<CallLog[]>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<CallLog[]>>(`/calls/search`, {
          params: query,
          headers: this.getAuthHeaders(),
        })
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
        pagination: response.pagination,
      };
    } catch (error) {
      console.error('Error searching calls:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to search calls',
      };
    }
  }

  public async searchConferences(query: CallHistoryQuery): Promise<ApiResponse<Conference[]>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<Conference[]>>(`/calls/conferences`, {
          params: query,
          headers: this.getAuthHeaders(),
        })
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
        pagination: response.pagination,
      };
    } catch (error) {
      console.error('Error searching conferences:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to search conferences',
      };
    }
  }

  public async getConferenceByCallSid(callSid: string): Promise<ApiResponse<Conference>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<Conference>>(`/calls/conference/${callSid}`, {
          headers: this.getAuthHeaders(),
        })
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching conference by call SID:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to fetch conference',
      };
    }
  }

  public async holdUnholdConference({
    hold,
    participantPhone,
    callLogId,
  }: {
    hold: boolean;
    callLogId: number;
    participantPhone: string;
  }): Promise<ApiResponse<void>> {
    try {
      const response = (
        await this.axios.put<ApiResponse<void>>(
          `/calls/participants/hold`,
          { hold, callLogId, participantPhone },
          {
            headers: this.getAuthHeaders(),
          },
        )
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating hold status:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to update hold status',
      };
    }
  }

  public async muteUnmuteConference({
    muted,
    participantPhone,
    callLogId,
  }: {
    muted: boolean;
    callLogId: number;
    participantPhone: string;
  }): Promise<ApiResponse<void>> {
    try {
      const response = (
        await this.axios.put<ApiResponse<void>>(
          `/calls/participants/mute`,
          { muted, callLogId, participantPhone },
          {
            headers: this.getAuthHeaders(),
          },
        )
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating mute status:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to update mute status',
      };
    }
  }

  public async transferConference(
    conferenceSid: string,
    data: TransferCallRequest,
  ): Promise<ApiResponse<CallLog>> {
    try {
      const response = (
        await this.axios.post<ApiResponse<CallLog>>(
          `/calls/conferences/${conferenceSid}/transfer`,
          data,
          {
            headers: this.getAuthHeaders(),
          },
        )
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      console.error('Error transferring conference:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to transfer conference',
      };
    }
  }

  public async initiateTransfer(
    conferenceFriendlyName: string,
    data: TransferCallRequest,
  ): Promise<ApiResponse<InitiateTransferResponse>> {
    try {
      const response = (
        await this.axios.post<ApiResponse<InitiateTransferResponse>>(
          `/calls/${conferenceFriendlyName}/transfer`,
          data,
          {
            headers: this.getAuthHeaders(),
          },
        )
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      const axiosError = error as AxiosError<ApiResponse<never>>;
      console.error('Error initiating transfer:', axiosError);
      return {
        success: false,
        statusCode: axiosError.response?.status || 500,
        message: axiosError.response?.data?.message || 'Failed to initiate transfer',
      };
    }
  }

  public async cancelTransfer(callLogId: string): Promise<ApiResponse<TransferActionResponse>> {
    try {
      const response = (
        await this.axios.post<ApiResponse<TransferActionResponse>>(
          `/calls/transfer/${callLogId}/cancel`,
          {},
          {
            headers: this.getAuthHeaders(),
          },
        )
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      const axiosError = error as AxiosError<ApiResponse<never>>;
      console.error('Error canceling transfer:', axiosError);
      return {
        success: false,
        statusCode: axiosError.response?.status || 500,
        message: axiosError.response?.data?.message || 'Failed to cancel transfer',
      };
    }
  }

  public async addNote(callId: number, data: AddNoteRequest): Promise<ApiResponse<ConferenceNote>> {
    try {
      const response = (
        await this.axios.post<ApiResponse<ConferenceNote>>(`/calls/${callId}/notes`, data, {
          headers: this.getAuthHeaders(),
        })
      ).data;

      if (response.statusCode && response.statusCode !== 201) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      console.error('Error adding note:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to add note',
      };
    }
  }

  public async getCallNotes(callId: number): Promise<ApiResponse<ConferenceNote[]>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<ConferenceNote[]>>(`/calls/${callId}/notes`, {
          headers: this.getAuthHeaders(),
        })
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      console.error('Error getting call notes:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to get call notes',
      };
    }
  }

  public async rateCall(callId: number, data: RateCallRequest): Promise<ApiResponse<CallLog>> {
    try {
      const response = (
        await this.axios.put<ApiResponse<CallLog>>(`/calls/${callId}/rating`, data, {
          headers: this.getAuthHeaders(),
        })
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      console.error('Error rating conference:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to rate conference',
      };
    }
  }

  public async updateCallPurpose(callId: number, purpose: string): Promise<ApiResponse<CallLog>> {
    try {
      const response = (
        await this.axios.put<ApiResponse<CallLog>>(
          `/calls/${callId}/purpose`,
          { purpose },
          {
            headers: this.getAuthHeaders(),
          },
        )
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating call purpose:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to update call purpose',
      };
    }
  }

  public async getAgentStatus(): Promise<ApiResponse<AgentAvailability>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<AgentAvailability>>(`/calls/agent/status`, {
          headers: this.getAuthHeaders(),
        })
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching agent status:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to fetch agent status',
      };
    }
  }

  public async updateAgentAvailability(
    data: UpdateAgentAvailabilityRequest,
  ): Promise<ApiResponse<AgentAvailability>> {
    try {
      const response = (
        await this.axios.put<ApiResponse<AgentAvailability>>(`/calls/agent/availability`, data, {
          headers: this.getAuthHeaders(),
        })
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating agent availability:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to update agent status',
      };
    }
  }

  /**
   * Updates agent availability using native fetch with `keepalive: true`.
   * This ensures the request completes even when the browser tab is closing
   * (beforeunload event), which axios does not support.
   */
  public updateAgentAvailabilityWithKeepalive(data: UpdateAgentAvailabilityRequest): void {
    const token = AuthService.getAuth()?.auth_token;
    if (!token || !APPOINTMENTS_API_BASE_URL) return;

    fetch(`${APPOINTMENTS_API_BASE_URL}/calls/agent/availability`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      keepalive: true,
    });
  }

  public async overrideAgentStatus(
    agentId: number,
    status: string,
  ): Promise<ApiResponse<AgentAvailability>> {
    try {
      const response = (
        await this.axios.put<ApiResponse<AgentAvailability>>(
          `/calls/agents/${agentId}/status`,
          { status },
          {
            headers: this.getAuthHeaders(),
          },
        )
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      };
    } catch (error) {
      console.error('Error overriding agent status:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to override agent status',
      };
    }
  }

  // === Metrics ===

  public async getCallMetrics(params: {
    startDate: string;
    endDate: string;
    compareStartDate?: string;
    compareEndDate?: string;
    agentId?: number;
  }): Promise<ApiResponse<CallMetricsResponse>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<CallMetricsResponse>>(`/calls/metrics`, {
          params,
          headers: this.getAuthHeaders(),
        })
      ).data;

      return response;
    } catch (error) {
      console.error('Error fetching call metrics:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to fetch call metrics',
      };
    }
  }

  public async getVoicemailMetrics(params: {
    startDate: string;
    endDate: string;
    compareStartDate?: string;
    compareEndDate?: string;
    agentId?: number;
  }): Promise<ApiResponse<any>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<any>>(`/voicemails/metrics`, {
          params,
          headers: this.getAuthHeaders(),
        })
      ).data;

      return response;
    } catch (error) {
      console.error('Error fetching voicemail metrics:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to fetch voicemail metrics',
      };
    }
  }

  public async getSmsMetrics(params: {
    startDate: string;
    endDate: string;
    compareStartDate?: string;
    compareEndDate?: string;
    agentId?: number;
  }): Promise<ApiResponse<any>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<any>>(`/communications/messages/metrics`, {
          params,
          headers: this.getAuthHeaders(),
        })
      ).data;

      return response;
    } catch (error) {
      console.error('Error fetching SMS metrics:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to fetch SMS metrics',
      };
    }
  }

  public async getAgentDirectory(): Promise<
    ApiResponse<{ [providerId: number]: AgentDirectoryEntry }>
  > {
    try {
      const response = (
        await this.axios.get<ApiResponse<RawAgentDirectoryEntry[]>>(`/calls/agent/directory`, {
          headers: this.getAuthHeaders(),
        })
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data
          ? response.data?.reduce((acc, entry) => {
              if (!acc[entry.coreProviderId]) {
                acc[entry.coreProviderId] = entry;
              }
              return acc;
            }, {} as Record<number, AgentDirectoryEntry>)
          : {},
      };
    } catch (error) {
      console.error('Error fetching agent directory:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to fetch agent directory',
      };
    }
  }

  public async searchAgents(
    params: SearchAgentsParams,
  ): Promise<ApiResponse<AgentDirectoryEntry[]>> {
    try {
      const response = (
        await this.axios.get<ApiResponse<AgentDirectoryEntry[]>>(`/calls/agents`, {
          params,
          headers: this.getAuthHeaders(),
        })
      ).data;

      if (response.statusCode && response.statusCode !== 200) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message,
        };
      }

      return {
        success: true,
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
        pagination: response.pagination,
      };
    } catch (error) {
      console.error('Error searching agents:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to search agents',
      };
    }
  }

  /**
   * Search voicemails with filtering and pagination
   */
  async searchVoicemails(params: FindAllVoicemailsParams): Promise<ApiResponse<VoicemailLog[]>> {
    try {
      const response = await this.axios.get<ApiResponse<VoicemailLog[]>>(`voicemails`, {
        params,
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error searching voicemails:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to search voicemails',
      };
    }
  }

  /**
   * Fetch one voicemail by id.
   *
   * Backs the `?voicemailId=` deep link in the voicemail notification email. Deliberately not a
   * lookup in the already-fetched list: the list defaults to PENDING and paginates, so the target
   * voicemail is often not there. The server applies the same clinic-scoping as the list, so an
   * out-of-scope id comes back 404.
   */
  async getVoicemailById(voicemailId: number): Promise<ApiResponse<VoicemailLog>> {
    try {
      const response = await this.axios.get<ApiResponse<VoicemailLog>>(`voicemails/${voicemailId}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching voicemail ${voicemailId}:`, error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to fetch voicemail',
      };
    }
  }

  /**
   * Update voicemail status or notes
   */
  async updateVoicemail(
    voicemailId: number,
    updateData: UpdateVoicemailParams,
  ): Promise<ApiResponse<VoicemailLog>> {
    try {
      const response = await this.axios.put<ApiResponse<VoicemailLog>>(
        `voicemails/${voicemailId}`,
        updateData,
        { headers: this.getAuthHeaders() },
      );

      return response.data;
    } catch (error) {
      console.error('Error updating voicemail:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to update voicemail',
      };
    }
  }

  /**
   * Get available agents for voicemail assignment
   */
  async getAvailableAgents(): Promise<ApiResponse<Agent[]>> {
    try {
      const response = await this.axios.get<ApiResponse<Agent[]>>(`voicemails/agents/available`, {
        headers: this.getAuthHeaders(),
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching available agents:', error);
      return {
        success: false,
        statusCode: 500,
        message: 'Failed to fetch available agents',
      };
    }
  }

  public async createTrackingNumber(
    data: CreateTrackingNumberRequest,
  ): Promise<ApiResponse<TrackingNumberDetail>> {
    try {
      const response = (
        await this.axios.post<TrackingNumberDetail>(`/tracking-numbers`, data, {
          headers: this.getAuthHeaders(),
        })
      ).data;

      return {
        success: true,
        statusCode: 201,
        data: response,
      };
    } catch (error: any) {
      console.error('Error creating tracking number:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to create tracking number',
      };
    }
  }

  public async listTrackingNumbers(
    params?: ListTrackingNumbersParams,
  ): Promise<ApiResponse<TrackingNumberListItem[]>> {
    try {
      const response = (
        await this.axios.get<TrackingNumberListItem[]>(`/tracking-numbers`, {
          params,
          headers: this.getAuthHeaders(),
        })
      ).data;

      return {
        success: true,
        statusCode: 200,
        data: response,
      };
    } catch (error: any) {
      console.error('Error fetching tracking numbers:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to fetch tracking numbers',
      };
    }
  }

  public async getTrackingNumberDetail(id: number): Promise<ApiResponse<TrackingNumberDetail>> {
    try {
      const response = (
        await this.axios.get<TrackingNumberDetail>(`/tracking-numbers/${id}`, {
          headers: this.getAuthHeaders(),
        })
      ).data;

      return {
        success: true,
        statusCode: 200,
        data: response,
      };
    } catch (error: any) {
      console.error('Error fetching tracking number detail:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to fetch tracking number detail',
      };
    }
  }

  public async updateTrackingNumber(
    id: number,
    data: UpdateTrackingNumberRequest,
  ): Promise<ApiResponse<TrackingNumberDetail>> {
    try {
      const response = (
        await this.axios.put<TrackingNumberDetail>(`/tracking-numbers/${id}`, data, {
          headers: this.getAuthHeaders(),
        })
      ).data;

      return {
        success: true,
        statusCode: 200,
        data: response,
      };
    } catch (error: any) {
      console.error('Error updating tracking number:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to update tracking number',
      };
    }
  }

  public async assignAgent(
    trackingNumberId: number,
    data: AssignAgentRequest,
  ): Promise<ApiResponse<TrackingNumberDetail>> {
    try {
      const response = (
        await this.axios.post<TrackingNumberDetail>(
          `/tracking-numbers/${trackingNumberId}/agents`,
          data,
          {
            headers: this.getAuthHeaders(),
          },
        )
      ).data;

      return {
        success: true,
        statusCode: 200,
        data: response,
      };
    } catch (error: any) {
      console.error('Error assigning agent:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to assign agent',
      };
    }
  }

  public async unassignAgent(
    trackingNumberId: number,
    data: UnassignAgentRequest,
  ): Promise<ApiResponse<TrackingNumberDetail>> {
    try {
      const response = (
        await this.axios.delete<TrackingNumberDetail>(
          `/tracking-numbers/${trackingNumberId}/agents`,
          {
            data,
            headers: this.getAuthHeaders(),
          },
        )
      ).data;

      return {
        success: true,
        statusCode: 200,
        data: response,
      };
    } catch (error: any) {
      console.error('Error unassigning agent:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to unassign agent',
      };
    }
  }
}

const voiceService = new VoiceService();

export default voiceService;
